import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  useMediaQuery,
  useTheme as useMuiTheme,
  ListItemIcon,
} from '@mui/material';
import {
  Menu as MenuIcon,
  BookOnline as BookingsIcon,
  Help as HelpIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Language as LanguageIcon,
  AccountCircle as AccountIcon,
  ExitToApp as LogoutIcon,
  Dashboard as DashboardIcon,
  SupervisorAccount as SupervisorIcon,
  Build as GarageIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  EventNote as AppointmentsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { useGarage } from '../contexts/GarageContext.jsx';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  // Add error boundary protection
  let user = null, logout = () => {}, isAuthenticated = false;
  try {
    const authContext = useAuth();
    user = authContext.user;
    logout = authContext.logout;
    isAuthenticated = authContext.isAuthenticated;
  } catch (error) {
    console.error('Auth context not available:', error);
  }
  const { mode, isDark, toggleTheme } = useTheme();
  const { t, language, changeLanguage, availableLanguages } = useLanguage();
  const { selectedGarage } = useGarage();
  
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);


  // Role-based menu items
  const getRoleBasedMenuItems = () => {
    if (!user) {
      return [
        { 
          text: t('nav.help'), 
          icon: <HelpIcon />, 
          path: '/help', 
          roles: ['public'] 
        },
      ];
    }

    switch (user.role) {
      case 'super_user':
        return [
          { 
            text: 'System Management', 
            icon: <SupervisorIcon />, 
            path: '/super-admin', 
            roles: ['super_user'] 
          },
        ];
        
      case 'garage_admin':
        return [
          { 
            text: 'Dashboard', 
            icon: <DashboardIcon />, 
            path: '/dashboard', 
            roles: ['garage_admin'] 
          },
        ];
        
      case 'customer':
      default:
        return [
          { 
            text: t('nav.bookings'), 
            icon: <BookingsIcon />, 
            path: '/bookings', 
            roles: ['customer'] 
          },
          { 
            text: t('nav.help'), 
            icon: <HelpIcon />, 
            path: '/help', 
            roles: ['customer'] 
          },
        ];
    }
  };

  const menuItems = getRoleBasedMenuItems();


  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    handleLanguageMenuClose();
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isActivePath = (path) => {
    if (path.includes('?')) {
      const [pathname, search] = path.split('?');
      return location.pathname === pathname && location.search.includes(search);
    }
    return location.pathname === path;
  };

  const currentLanguage = availableLanguages.find(lang => lang.code === language);

  return (
    <>
      <AppBar position="sticky" elevation={1}>
        <Toolbar>
          
          <Typography
            variant="h6"
            component="div"
            sx={{ 
              flexGrow: 1, 
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            Nexfah <Box component="span" sx={{ fontSize: '0.8em', fontWeight: 'normal' }}>motoring</Box>
          </Typography>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              {menuItems.map((item) => {
                const isVisible = item.roles.includes('public') || (isAuthenticated && user && item.roles.includes(user.role));
                const isActive = isActivePath(item.path);
                
                if (!isVisible) return null;
                
                return (
                  <Button
                    key={item.text}
                    color="inherit"
                    onClick={() => navigate(item.path)}
                    sx={{
                      fontWeight: isActive ? 'bold' : 'normal',
                      backgroundColor: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                    }}
                  >
                    {item.text}
                  </Button>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Language Selector */}
            <IconButton
              color="inherit"
              onClick={handleLanguageMenuOpen}
            >
              <LanguageIcon />
              {!isMobile && currentLanguage && (
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  {currentLanguage.flag}
                </Typography>
              )}
            </IconButton>

            {/* Theme Toggle */}
            <IconButton color="inherit" onClick={toggleTheme}>
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>

            {/* User Menu */}
            {isAuthenticated ? (
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
              >
                {user?.picture_url ? (
                  <Avatar
                    src={user.picture_url}
                    alt={user.name}
                    sx={{ width: 32, height: 32 }}
                  />
                ) : (
                  <AccountIcon />
                )}
              </IconButton>
            ) : (
              <Button
                color="inherit"
                variant="outlined"
                onClick={handleLogin}
                sx={{ 
                  borderColor: 'inherit',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                  minWidth: { xs: 'auto', sm: '64px' },
                  '&:hover': {
                    borderColor: 'inherit',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {t('nav.signIn')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>


      {/* Language Menu */}
      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={handleLanguageMenuClose}
      >
        {availableLanguages.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={language === lang.code}
            onClick={() => handleLanguageChange(lang.code)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>{lang.flag}</Typography>
              <Typography>{lang.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
      >
        {user && [
          <Box key="user-info" sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {user.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
            <Typography variant="caption" sx={{ 
              display: 'inline-block',
              mt: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: user.role === 'super_user' ? 'error.main' : 
                              user.role === 'garage_admin' ? 'primary.main' : 'success.main',
              color: 'white',
              textTransform: 'uppercase',
              fontSize: '0.65rem'
            }}>
              {user.role === 'super_user' ? 'Super Admin' : 
               user.role === 'garage_admin' ? 'Garage Admin' : 'User'}
            </Typography>
            {user.role === 'garage_admin' && selectedGarage && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 'medium' }}>
                {selectedGarage.name}
              </Typography>
            )}
          </Box>,
          <Box key="divider" sx={{ borderTop: 1, borderColor: 'divider', my: 1 }} />
        ]}
        
        {/* Role-specific menu items */}
        {user?.role === 'customer' && (
          <MenuItem onClick={() => { handleNavigation('/bookings'); handleUserMenuClose(); }}>
            <ListItemIcon>
              <BookingsIcon fontSize="small" />
            </ListItemIcon>
            {t('nav.bookings')}
          </MenuItem>
        )}
        
        {user?.role === 'garage_admin' && [
          <MenuItem key="dashboard" onClick={() => { handleNavigation('/dashboard'); handleUserMenuClose(); }}>
            <ListItemIcon>
              <DashboardIcon fontSize="small" />
            </ListItemIcon>
            Dashboard
          </MenuItem>,
          <MenuItem key="appointments" onClick={() => { handleNavigation('/garage-admin?view=appointments'); handleUserMenuClose(); }}>
            <ListItemIcon>
              <AppointmentsIcon fontSize="small" />
            </ListItemIcon>
            Appointments
          </MenuItem>,
          <MenuItem key="calendar" onClick={() => { handleNavigation('/garage-admin?view=calendar'); handleUserMenuClose(); }}>
            <ListItemIcon>
              <CalendarIcon fontSize="small" />
            </ListItemIcon>
            Calendar
          </MenuItem>,
          <MenuItem key="configuration" onClick={() => { handleNavigation('/garage-admin?view=configuration'); handleUserMenuClose(); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Configuration
          </MenuItem>
        ]}
        
        {user?.role === 'super_user' && (
          <MenuItem onClick={() => { handleNavigation('/super-admin'); handleUserMenuClose(); }}>
            <ListItemIcon>
              <SupervisorIcon fontSize="small" />
            </ListItemIcon>
            System Management
          </MenuItem>
        )}
        
        <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1 }} />
        
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('nav.signOut')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default Navigation;